/* LT-AuditDrawer-02 \u2014 Tender history drawer.
 *
 * Right-side Shadcn Sheet (\u2248520px). Opened by HistoryButton or by URL
 * deep-link (?history=open). Closes via Esc / backdrop / X \u2014 clears the URL
 * query param on close.
 *
 * Data:
 *   GET /api/tenders/{tender_id}/audit?page=N&page_size=50&event_types=&date_from=&date_to=
 *   Response shape contracted by /app/backend/routes/audit.py.
 *
 * Filtering:
 *   \u2022 event_type multi-select grouped by `category`, rendered as a Popover
 *     of checkboxes. Options sourced from response.event_types (never
 *     hardcoded).
 *   \u2022 From/To date inputs use native <input type="date"> for keyboard +
 *     accessibility win; visual format on hover tooltip is UK DD/MM/YYYY.
 *   \u2022 'Clear filters' resets all 3 dimensions in one go.
 *
 * Pagination:
 *   50 per page. "Load more" appends to existing items (scroll preserved
 *   naturally because we never reset the list). has_more drives button.
 *
 * States:
 *   skeleton (first load), empty (filter returns 0), normal, loading-more.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Skeleton } from "./ui/skeleton";
import {
  Clock, History, Filter, X, ChevronDown, RefreshCw,
  // every icon_key the formatter can emit:
  FilePlus2, FileEdit, FileX, FileText, FileCheck,
  LayoutList, ArrowUpDown, LayoutTemplate, PackagePlus, Upload,
  Sparkles, Plus, Trash2, RotateCcw, Edit3,
  CheckSquare, Square, Percent, AlertTriangle, Layers, Ruler,
  ToggleRight, Activity, Scale, Hammer, Receipt, ClipboardList,
  LogIn, LogOut, Archive, Copy, BookOpen, Settings, RefreshCw as Sync,
  FileDown, FileSpreadsheet,
} from "lucide-react";
import { api } from "../lib/api";
import { relTime, ukDateTime } from "../lib/format";
import { toast } from "sonner";

const cls = (...a) => a.filter(Boolean).join(" ");

/* icon_key (from formatter) -> Lucide component */
const ICON_MAP = {
  FilePlus2, FileEdit, FileX, FileText, FileCheck,
  LayoutList, ArrowUpDown, LayoutTemplate, PackagePlus, Upload,
  Sparkles, Plus, Trash2, RotateCcw, Edit3,
  CheckSquare, Square, Percent, AlertTriangle, Layers, Ruler,
  ToggleRight, Activity, Scale, Hammer, Receipt, ClipboardList,
  LogIn, LogOut, Archive, Copy, BookOpen, Settings, Clock,
  RefreshCw, FileDown, FileSpreadsheet,
};

/* Subtle tone per category \u2014 left-border accent on each row */
const CATEGORY_TONE = {
  tender:        { bg: "#F0EFEB", text: "#5C5750", border: "#C8C0B0" },
  phases_lines:  { bg: "#EEF2F7", text: "#3F526B", border: "#BCCBDA" },
  prelims:       { bg: "#F0EEF6", text: "#52447A", border: "#C9C0E0" },
  provsums:      { bg: "#EFF4ED", text: "#3D5740", border: "#C4D5C5" },
  ao:            { bg: "#F5F1E6", text: "#7A6431", border: "#DBC890" },
  margin:        { bg: "#F1EBE2", text: "#7A532A", border: "#D6B894" },
  adjudication:  { bg: "#F4ECE9", text: "#7A4031", border: "#DCAEA0" },
  outputs:       { bg: "#EAF2F1", text: "#365752", border: "#B9D4CF" },
  subby:         { bg: "#F2EEED", text: "#665649", border: "#CABDB1" },
  sync:          { bg: "#ECEEEC", text: "#445245", border: "#BBC4BB" },
  admin:         { bg: "#F1EFEC", text: "#67625A", border: "#C8C2B5" },
};

const PAGE_SIZE = 50;


/* ---------- Trigger button + count badge ---------- */
export function HistoryButton({ tenderId, className = "" }) {
  const [count, setCount] = useState(null);
  const [params, setParams] = useSearchParams();
  // cheap count fetch on mount + window focus
  const fetchCount = () => {
    api.get(`/tenders/${tenderId}/audit`, { params: { page_size: 1 } })
      .then((r) => setCount(r.data.total ?? 0))
      .catch(() => setCount(null));
  };
  useEffect(() => { fetchCount(); }, [tenderId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => fetchCount();
    window.addEventListener("focus", h);
    return () => window.removeEventListener("focus", h);
  }, [tenderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = () => {
    const next = new URLSearchParams(params);
    next.set("history", "open");
    setParams(next, { replace: false });
  };
  return (
    <button
      onClick={open}
      data-testid="tender-history-button"
      className={cls(
        "inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-surface text-ink-2",
        "hover:bg-surface-2 transition-colors",
        className,
      )}
      style={{ fontSize: 12 }}
      title={count != null ? `${count} audit ${count === 1 ? "entry" : "entries"}` : "Open change history"}
    >
      <Clock size={13} />
      <span style={{ fontWeight: 500 }}>History</span>
      {count != null && count > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-ink/10 text-ink-2 tabular-nums"
          style={{ fontSize: 10, fontWeight: 600, padding: "0 5px" }}
          data-testid="tender-history-count-badge"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}


/* ---------- The drawer itself ---------- */
export function AuditDrawer({ tenderId, tenderRef }) {
  const [params, setParams] = useSearchParams();
  const isOpen = params.get("history") === "open";

  // ---- state ----
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [eventTypeCatalogue, setEventTypeCatalogue] = useState([]);
  const [filters, setFilters] = useState({
    event_types: [],   // string[]
    date_from:   "",   // YYYY-MM-DD
    date_to:     "",   // YYYY-MM-DD
  });

  // ---- close handler ----
  const onOpenChange = (open) => {
    if (!open) {
      const next = new URLSearchParams(params);
      next.delete("history");
      setParams(next, { replace: false });
    }
  };

  // ---- fetcher ----
  const fetchPage = async (pageN, opts = {}) => {
    const append = opts.append === true;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const q = {
        page: pageN,
        page_size: PAGE_SIZE,
      };
      if (filters.event_types.length > 0) q.event_types = filters.event_types.join(",");
      if (filters.date_from) q.date_from = filters.date_from;
      if (filters.date_to)   q.date_to   = filters.date_to;
      const r = await api.get(`/tenders/${tenderId}/audit`, { params: q });
      const d = r.data;
      setTotal(d.total);
      setHasMore(d.has_more);
      setCategories(d.categories || []);
      setEventTypeCatalogue(d.event_types || []);
      setItems((prev) => (append ? [...prev, ...d.items] : d.items));
      setPage(pageN);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // ---- refetch on open + on filter change ----
  useEffect(() => {
    if (isOpen) {
      fetchPage(1, { append: false });
    }
    // reset when closed
    if (!isOpen) {
      setItems([]);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenderId]);

  useEffect(() => {
    if (isOpen) fetchPage(1, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.event_types.join(","), filters.date_from, filters.date_to]);

  // ---- group items by date for visual hierarchy ----
  const groupedItems = useMemo(() => groupByDay(items), [items]);

  const filtersActive = (
    filters.event_types.length > 0 || filters.date_from || filters.date_to
  );

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[540px] flex flex-col p-0"
        data-testid="audit-drawer"
      >
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-line shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <SheetTitle className="flex items-center gap-2 text-ink"
                style={{ fontSize: 16, fontWeight: 600 }}>
                <History size={16} className="text-ink-3" />
                Change history
              </SheetTitle>
              <SheetDescription className="text-ink-4 mt-0.5" style={{ fontSize: 11.5 }}>
                {tenderRef ? `${tenderRef} · ` : ""}
                {total > 0
                  ? `${total} ${total === 1 ? "entry" : "entries"}${filtersActive ? " (filtered)" : ""}`
                  : "—"}
              </SheetDescription>
            </div>
            <button
              onClick={() => fetchPage(1, { append: false })}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-ink-3 hover:bg-surface-2"
              aria-label="Refresh"
              data-testid="audit-drawer-refresh"
              title="Refresh"
            >
              <RefreshCw size={13} />
            </button>
          </div>
        </SheetHeader>

        {/* Filter bar (sticky under header) */}
        <FilterBar
          eventTypeCatalogue={eventTypeCatalogue}
          categories={categories}
          filters={filters}
          setFilters={setFilters}
        />

        {/* Body \u2014 scrollable */}
        <div className="flex-1 overflow-y-auto" data-testid="audit-drawer-body">
          {loading && items.length === 0 ? (
            <SkeletonRows />
          ) : items.length === 0 ? (
            <EmptyState filtersActive={filtersActive} />
          ) : (
            <>
              {groupedItems.map(({ dayLabel, rows }) => (
                <div key={dayLabel}>
                  <DaySeparator label={dayLabel} />
                  {rows.map((r) => (
                    <AuditRow key={r.id} row={r} />
                  ))}
                </div>
              ))}
              {hasMore && (
                <div className="px-5 py-4 border-t border-line/50">
                  <button
                    onClick={() => fetchPage(page + 1, { append: true })}
                    disabled={loadingMore}
                    className={cls(
                      "w-full h-9 rounded-md text-ink-2 border border-line",
                      loadingMore ? "opacity-50 cursor-not-allowed" : "hover:bg-surface-2",
                    )}
                    style={{ fontSize: 12, fontWeight: 500 }}
                    data-testid="audit-drawer-load-more"
                  >
                    {loadingMore ? "Loading…" : `Load more (${total - items.length} remaining)`}
                  </button>
                </div>
              )}
              {!hasMore && items.length > 10 && (
                <div className="px-5 py-6 text-center text-ink-4" style={{ fontSize: 11 }}>
                  End of history.
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}


/* ---------- Filter bar ---------- */
function FilterBar({ eventTypeCatalogue, categories, filters, setFilters }) {
  const eventTypesByCategory = useMemo(() => {
    const out = {};
    for (const e of eventTypeCatalogue) {
      if (!out[e.category]) out[e.category] = [];
      out[e.category].push(e);
    }
    return out;
  }, [eventTypeCatalogue]);

  const selectedCount = filters.event_types.length;
  const datesActive = filters.date_from || filters.date_to;
  const anyActive = selectedCount > 0 || datesActive;

  const toggleEvent = (key) => {
    setFilters((f) => ({
      ...f,
      event_types: f.event_types.includes(key)
        ? f.event_types.filter((k) => k !== key)
        : [...f.event_types, key],
    }));
  };

  const toggleCategory = (catKey) => {
    const evs = (eventTypesByCategory[catKey] || []).map((e) => e.key);
    const allSelected = evs.every((k) => filters.event_types.includes(k));
    setFilters((f) => ({
      ...f,
      event_types: allSelected
        ? f.event_types.filter((k) => !evs.includes(k))
        : [...new Set([...f.event_types, ...evs])],
    }));
  };

  const clearAll = () => setFilters({ event_types: [], date_from: "", date_to: "" });

  return (
    <div className="px-5 py-3 border-b border-line bg-surface-2/30 shrink-0 space-y-2.5"
         data-testid="audit-drawer-filter-bar">
      <div className="flex items-center gap-2">
        {/* Event-type popover */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 11.5 }}
              data-testid="audit-drawer-filter-events"
            >
              <Filter size={12} />
              {selectedCount > 0 ? `${selectedCount} event ${selectedCount === 1 ? "type" : "types"}` : "All event types"}
              <ChevronDown size={11} className="text-ink-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0 max-h-[420px] overflow-y-auto">
            {categories.filter((c) => eventTypesByCategory[c.key]?.length).map((c) => {
              const evs = eventTypesByCategory[c.key] || [];
              const selectedInCat = evs.filter((e) => filters.event_types.includes(e.key)).length;
              const allInCat = selectedInCat === evs.length;
              return (
                <div key={c.key} className="border-b border-line/40 last:border-0">
                  <button
                    onClick={() => toggleCategory(c.key)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-surface-2/40 hover:bg-surface-2 text-left"
                    style={{ fontSize: 11 }}
                  >
                    <span className="col-head text-ink-2" style={{ fontSize: 10 }}>{c.label}</span>
                    <span className="text-ink-4 tabular-nums" style={{ fontSize: 10 }}>
                      {allInCat ? "all" : selectedInCat > 0 ? `${selectedInCat}/${evs.length}` : `${evs.length}`}
                    </span>
                  </button>
                  {evs.map((e) => (
                    <label key={e.key}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-2/60 cursor-pointer"
                      style={{ fontSize: 12 }}
                    >
                      <input
                        type="checkbox"
                        checked={filters.event_types.includes(e.key)}
                        onChange={() => toggleEvent(e.key)}
                        className="rounded text-accent"
                        data-testid={`audit-drawer-filter-event-${e.key}`}
                      />
                      <span className="text-ink-2 truncate">{e.label}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* Date pickers */}
        <div className="flex items-center gap-1.5 ml-auto">
          <input
            type="date"
            value={filters.date_from}
            max={filters.date_to || undefined}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            className="h-8 px-2 rounded-md border border-line bg-surface text-ink-2 focus:outline-none focus:ring-1 focus:ring-accent"
            style={{ fontSize: 11 }}
            placeholder="From"
            data-testid="audit-drawer-filter-date-from"
            title={filters.date_from ? `From ${formatDateInput(filters.date_from)}` : "Filter: from date"}
          />
          <span className="text-ink-4" style={{ fontSize: 11 }}>→</span>
          <input
            type="date"
            value={filters.date_to}
            min={filters.date_from || undefined}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            className="h-8 px-2 rounded-md border border-line bg-surface text-ink-2 focus:outline-none focus:ring-1 focus:ring-accent"
            style={{ fontSize: 11 }}
            placeholder="To"
            data-testid="audit-drawer-filter-date-to"
            title={filters.date_to ? `To ${formatDateInput(filters.date_to)}` : "Filter: to date"}
          />
        </div>
      </div>
      {anyActive && (
        <button
          onClick={clearAll}
          className="inline-flex items-center gap-1 text-accent hover:underline"
          style={{ fontSize: 11 }}
          data-testid="audit-drawer-clear-filters"
        >
          <X size={11} /> Clear filters
        </button>
      )}
    </div>
  );
}


/* ---------- Row ---------- */
function AuditRow({ row }) {
  const Icon = ICON_MAP[row.icon_key] || Activity;
  const tone = CATEGORY_TONE[row.category] || CATEGORY_TONE.admin;
  const ts = row.timestamp ? new Date(row.timestamp) : null;
  return (
    <div
      className="px-5 py-3 border-b border-line/40 last:border-0 hover:bg-surface-2/30 transition-colors"
      data-testid={`audit-row-${row.id}`}
      data-event-type={row.event_type}
      data-category={row.category}
    >
      <div className="flex gap-3">
        <div
          className="shrink-0 flex items-center justify-center rounded-md"
          style={{
            width: 26, height: 26,
            background: tone.bg, color: tone.text,
            border: `1px solid ${tone.border}`,
          }}
        >
          <Icon size={13} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 }}>
            {row.description}
          </div>
          <div
            className="text-ink-4 mt-1 flex items-center gap-1.5"
            style={{ fontSize: 11 }}
            title={ts ? ukDateTime(row.timestamp) : ""}
          >
            <span className="truncate">{row.user_name || "Unknown user"}</span>
            <span>·</span>
            <span className="tabular-nums">{ts ? relTime(row.timestamp) : ""}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------- States ---------- */
function SkeletonRows() {
  return (
    <div className="py-2" data-testid="audit-drawer-skeleton">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="px-5 py-3 border-b border-line/40 flex gap-3">
          <Skeleton className="h-[26px] w-[26px] rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-[80%]" />
            <Skeleton className="h-2 w-[45%]" />
          </div>
        </div>
      ))}
    </div>
  );
}


function EmptyState({ filtersActive }) {
  return (
    <div className="px-8 py-16 text-center" data-testid="audit-drawer-empty">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface-2 text-ink-4 mb-3">
        <History size={20} />
      </div>
      <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
        {filtersActive ? "No matching changes" : "No changes recorded yet"}
      </div>
      <div className="text-ink-4 mt-1.5" style={{ fontSize: 11.5 }}>
        {filtersActive
          ? "Try clearing the filters or widening the date range."
          : "Edits to this tender will appear here in real time."}
      </div>
    </div>
  );
}


/* ---------- Helpers ---------- */
function DaySeparator({ label }) {
  return (
    <div
      className="px-5 py-1.5 bg-surface-2/40 border-b border-line/40 sticky top-0 z-10"
      style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}
    >
      <span className="text-ink-3">{label}</span>
    </div>
  );
}


function groupByDay(items) {
  const groups = new Map();
  for (const it of items) {
    if (!it.timestamp) continue;
    const d = new Date(it.timestamp);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const label = dayLabelFor(d);
    if (!groups.has(key)) groups.set(key, { dayLabel: label, rows: [] });
    groups.get(key).rows.push(it);
  }
  return Array.from(groups.values());
}


function dayLabelFor(d) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}


function formatDateInput(yyyyMmDd) {
  if (!yyyyMmDd || yyyyMmDd.length !== 10) return yyyyMmDd;
  const [y, m, d] = yyyyMmDd.split("-");
  return `${d}/${m}/${y}`;
}
